import { useMemo } from 'react';
import { AlertCircle, CheckCircle2, ImageOff } from 'lucide-react';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { ConflictDecision, ParsedRow } from './parseImportZip';

interface Props {
  rows: ParsedRow[];
  imageBlobs: Map<string, Blob>;
  onDecisionChange: (rowIndex: number, decision: ConflictDecision) => void;
}

export function ImportPreviewTable({ rows, imageBlobs, onDecisionChange }: Props) {
  const t = useT();

  const blobUrls = useMemo(() => {
    const map = new Map<string, string>();
    imageBlobs.forEach((blob, name) => {
      map.set(name, URL.createObjectURL(blob));
    });
    return map;
  }, [imageBlobs]);

  return (
    <div className="overflow-auto rounded-lg border border-[var(--color-xk-border)]">
      <table className="min-w-full text-left text-[12px]">
        <thead className="sticky top-0 bg-[var(--color-xk-bg-deep)] text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--color-xk-text-muted)]">
          <tr>
            <th className="px-3 py-2.5">{t('stock.import.col.image')}</th>
            <th className="px-3 py-2.5">{t('stock.import.col.name')}</th>
            {/* Classification column hidden — every imported row gets the
                default value (see parseImportZip). */}
            <th className="px-3 py-2.5">{t('stock.import.col.rating')}</th>
            <th className="px-3 py-2.5">{t('stock.import.col.qty')}</th>
            <th className="px-3 py-2.5">{t('stock.import.col.status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <PreviewRow
              key={row.rowIndex}
              row={row}
              previewUrl={
                row.imageFilename ? blobUrls.get(row.imageFilename.toLowerCase()) : undefined
              }
              fallbackUrl={row.payload.image}
              onDecisionChange={onDecisionChange}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PreviewRow({
  row,
  previewUrl,
  fallbackUrl,
  onDecisionChange,
}: {
  row: ParsedRow;
  previewUrl?: string;
  fallbackUrl?: string;
  onDecisionChange: (rowIndex: number, decision: ConflictDecision) => void;
}) {
  const t = useT();
  const hasErrors = row.errors.length > 0;
  const isDuplicate = !!row.duplicateOf;

  const thumb = previewUrl || fallbackUrl;

  return (
    <tr
      className={cn(
        'border-t border-[var(--color-xk-border)]',
        hasErrors ? 'bg-[var(--color-error)]/10' : 'hover:bg-[var(--color-xk-surface)]/40',
      )}
    >
      <td className="px-3 py-2.5">
        <div className="grid h-10 w-10 place-items-center overflow-hidden rounded bg-[var(--color-xk-surface)]">
          {thumb ? (
            <img src={resolveImageUrl(thumb)} alt="" className="h-10 w-10 object-cover" />
          ) : (
            <ImageOff className="h-4 w-4 text-[var(--color-xk-text-muted)]" />
          )}
        </div>
      </td>
      <td className="px-3 py-2.5">
        <div className="font-semibold text-[var(--color-xk-lavender)]">{row.payload.name}</div>
        <div className="text-[10px] text-[var(--color-xk-text-muted)]">
          {t('stock.import.col.status').toLowerCase()} row {row.rowIndex}
        </div>
      </td>
      {/* Classification cell hidden alongside its header. */}
      <td className="px-3 py-2.5 text-[var(--color-xk-text-muted)]">
        {row.payload.rating}
      </td>
      <td className="px-3 py-2.5 text-[var(--color-xk-text-muted)]">
        {row.payload.initialQuantity ?? '—'}
      </td>
      <td className="px-3 py-2.5">
        {hasErrors ? (
          <ErrorBadge errors={row.errors} />
        ) : isDuplicate ? (
          <DuplicateControls row={row} onDecisionChange={onDecisionChange} />
        ) : (
          <span className="inline-flex items-center gap-1 text-[var(--color-success)]">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span className="text-[11px] font-bold">{t('stock.import.status.new')}</span>
          </span>
        )}
      </td>
    </tr>
  );
}

function ErrorBadge({ errors }: { errors: string[] }) {
  const t = useT();
  return (
    <div className="flex items-start gap-1.5">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-none text-[var(--color-error)]" />
      <div>
        <div className="text-[11px] font-bold text-[var(--color-error)]">
          {t('stock.import.status.error')}
        </div>
        <ul className="mt-0.5 space-y-0.5 text-[10px] text-[var(--color-error)]/90">
          {errors.map((e, i) => (
            <li key={i}>{formatError(e, t)}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function DuplicateControls({
  row,
  onDecisionChange,
}: {
  row: ParsedRow;
  onDecisionChange: (rowIndex: number, decision: ConflictDecision) => void;
}) {
  const t = useT();
  return (
    <div>
      <div className="mb-1 text-[11px] font-bold text-[var(--color-accent-ink)]">
        {t('stock.import.status.duplicate')}
      </div>
      <div className="flex flex-col gap-0.5 text-[10px]">
        <label className="flex cursor-pointer items-center gap-1.5 text-[var(--color-xk-lavender)]">
          <input
            type="radio"
            name={`decision-${row.rowIndex}`}
            checked={row.decision === 'replace'}
            onChange={() => onDecisionChange(row.rowIndex, 'replace')}
            className="accent-[var(--color-xk-accent)]"
          />
          {t('stock.import.action.replace')}
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-[var(--color-xk-lavender)]">
          <input
            type="radio"
            name={`decision-${row.rowIndex}`}
            checked={row.decision === 'createNew'}
            onChange={() => onDecisionChange(row.rowIndex, 'createNew')}
            className="accent-[var(--color-xk-accent)]"
          />
          {t('stock.import.action.createNew')}
        </label>
      </div>
    </div>
  );
}

function formatError(code: string, t: (k: string) => string): string {
  if (code.startsWith('imageNotFound:')) {
    return t('stock.import.error.imageNotFound').replace('{file}', code.slice('imageNotFound:'.length));
  }
  if (code.startsWith('logoNotFound:')) {
    return t('stock.import.error.logoNotFound').replace('{file}', code.slice('logoNotFound:'.length));
  }
  const key = `stock.import.error.${code}`;
  const translated = t(key);
  return translated === key ? code : translated;
}
